import React, { useState, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { hu } from "date-fns/locale";
import { toast } from "react-toastify";
import "./CustomDateHourPicker.scss";

const CustomDateHourPicker = ({
  onDateHourChange,
  initialDate = new Date(),
  initialHour = new Date().getHours() - 1,
}) => {
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedHour, setSelectedHour] = useState(initialHour);
  const today = new Date();

  // Returns true if the selected date is today
  const isToday = (date) => {
    if (!date) return false;
    const d = new Date(date);
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  };

  // Max selectable hour: if today → current hour - 1, otherwise 23
  const getMaxHour = (date) => {
    if (isToday(date)) {
      return today.getHours() - 1; // e.g. at 9:17 → max is 8
    }
    return 23;
  };

  const handleDayClick = (day) => {
    const dayTimestamp = new Date(day).setHours(23, 59, 59, 999);
    const todayTimestamp = new Date().setHours(23, 59, 59, 999);

    if (dayTimestamp > todayTimestamp) {
      toast.error("A kiválasztott dátum nem lehet a jövőben!");
      return;
    }

    setSelectedDate(day);

    // If switching to today and selected hour is too late, clamp it
    if (isToday(day)) {
      const maxHour = today.getHours() - 1;
      if (selectedHour > maxHour) {
        setSelectedHour(maxHour);
      }
    }
  };

  const handleHourChange = (e) => {
    const hour = parseInt(e.target.value, 10);
    const maxHour = getMaxHour(selectedDate);

    if (hour > maxHour) {
      toast.error(`Csak ${maxHour}:00-ig választhat órát a mai napra!`);
      return;
    }
    setSelectedHour(hour);
  };

  const handleLatestHour = () => {
    const now = new Date();
    const latestHour = now.getHours() - 1; // always current - 1
    setSelectedDate(now);
    setSelectedHour(latestHour);

    if (onDateHourChange) {
      onDateHourChange(now, latestHour);
    }
  };

  const handleApply = () => {
    if (onDateHourChange) {
      onDateHourChange(selectedDate, selectedHour);
    }
  };

  // Generate hour options — disable future hours if today is selected
  const maxHour = getMaxHour(selectedDate);
  const hourOptions = [];
  for (let i = 0; i < 24; i++) {
    hourOptions.push(
      <option key={i} value={i} disabled={i > maxHour}>
        {i.toString().padStart(2, "0")}:00
      </option>,
    );
  }

  return (
    <div className="date-hour-picker-container">
      <div className="date-hour-picker">
        <div className="calendar-section">
          <DayPicker
            selected={selectedDate}
            onDayClick={handleDayClick}
            locale={hu}
            showOutsideDays
            numberOfMonths={1}
            mode="single"
            captionLayout="dropdown"
            startMonth={new Date(2022, 0)}
            endMonth={new Date(2026, 11)}
            modifiers={{
              selected: [selectedDate],
            }}
            modifiersStyles={{
              selected: {
                backgroundColor: "#F3CA52",
                color: "#333",
                fontWeight: "800",
              },
            }}
            footer={
              selectedDate ? (
                <div className="selected-date-info">
                  Dátum: <strong>{selectedDate.toLocaleDateString()}</strong>
                  {isToday(selectedDate) && (
                    <span
                      className="text-muted ms-2"
                      style={{ fontSize: "0.8rem" }}
                    >
                      (max: {maxHour}:00)
                    </span>
                  )}
                </div>
              ) : (
                "Válasszon dátumot"
              )
            }
          />
        </div>

        <div className="hour-selection-section">
          <div className="hour-selector">
            <label>Óra (UTC):</label>
            <select value={selectedHour} onChange={handleHourChange}>
              {hourOptions}
            </select>
          </div>

          <div className="button-group">
            <button className="apply-button" onClick={handleApply}>
              Alkalmaz
            </button>

            <button className="latest-hour-button" onClick={handleLatestHour}>
              Legfrissebb óra
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDateHourPicker;
